// AllStudents.jsx
import React, { useState } from "react";
import {
  Button,
  Card,
  Table,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Container,
  Alert,
} from "react-bootstrap";
import { useStudentContext } from "../../contextProviders/studentContext";
import { useUser } from "../../contextProviders/userContext";
import NotificationIcon from "../../components/Notification";
import { FiEye } from "react-icons/fi";
import Sidebar from "../../components/SideBar";
import TopNavBar from "../../components/topNavBar";
import { useNavigate } from "react-router-dom";

const AdminStudentsView = () => {
  const { students, loading, error, updateStudentRole, resetStudentPassword, createAdminUser, deleteUserAccount } = useStudentContext();
  const navigate = useNavigate();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { user } = useUser();

  const [searchTerm, setSearchTerm] = useState("");
  const [facultyFilter, setFacultyFilter] = useState("");
  const [degreeFilter, setDegreeFilter] = useState("");
  
  // Separation Tab state: "student" or "admin"
  const [activeTab, setActiveTab] = useState("student");

  // Admin Actions State
  const [resetPasswordVal, setResetPasswordVal] = useState("");
  const [actionFeedback, setActionFeedback] = useState({ type: "", message: "" });
  const [actionLoading, setActionLoading] = useState(false);

  // Create Admin State
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [newAdminData, setNewAdminData] = useState({
    name: "",
    surname: "",
    email: "",
    password: "",
  });
  const [createAdminFeedback, setCreateAdminFeedback] = useState({ type: "", message: "" });
  const [createAdminLoading, setCreateAdminLoading] = useState(false);

  // Delete User Confirmation State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");

  const handleCreateAdminSubmit = async (e) => {
    e.preventDefault();
    setCreateAdminLoading(true);
    setCreateAdminFeedback({ type: "", message: "" });

    // Validate email format
    const staffEmailRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+@wits\.ac\.za$/;
    if (!staffEmailRegex.test(newAdminData.email)) {
      setCreateAdminFeedback({
        type: "danger",
        message: "Invalid staff email. Staff emails must be in the format 'name.surname@wits.ac.za'.",
      });
      setCreateAdminLoading(false);
      return;
    }

    try {
      await createAdminUser(newAdminData);
      setCreateAdminFeedback({
        type: "success",
        message: "Admin account created successfully!",
      });
      setNewAdminData({ name: "", surname: "", email: "", password: "" });
      setTimeout(() => {
        setShowCreateAdminModal(false);
        setCreateAdminFeedback({ type: "", message: "" });
      }, 1500);
    } catch (err) {
      setCreateAdminFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to create admin account.",
      });
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const handleDeleteUserSubmit = async () => {
    if (!selectedStudent) return;
    setActionLoading(true);
    setActionFeedback({ type: "", message: "" });
    try {
      await deleteUserAccount(selectedStudent._id);
      setActionFeedback({
        type: "success",
        message: "User account and associated data successfully deleted!",
      });
      setShowDeleteConfirm(false);
      setDeleteConfirmInput("");
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (err) {
      setActionFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to delete user account.",
      });
      setShowDeleteConfirm(false);
      setDeleteConfirmInput("");
    } finally {
      setActionLoading(false);
    }
  };

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
    setActionFeedback({ type: "", message: "" });
    setResetPasswordVal("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
    setActionFeedback({ type: "", message: "" });
    setResetPasswordVal("");
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good Morning";
    if (hour < 18) return "Good Afternoon";
    return "Good Evening";
  };

  const handleRoleToggle = async () => {
    if (!selectedStudent) return;
    const targetRole = selectedStudent.role === "admin" ? "student" : "admin";
    setActionLoading(true);
    setActionFeedback({ type: "", message: "" });
    try {
      await updateStudentRole(selectedStudent._id, targetRole);
      setActionFeedback({
        type: "success",
        message: `Successfully updated user privilege to ${targetRole}!`,
      });
      // Update local modal state
      setSelectedStudent(prev => ({ ...prev, role: targetRole }));
    } catch (err) {
      setActionFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to update user privilege.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdminResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedStudent || !resetPasswordVal) return;
    setActionLoading(true);
    setActionFeedback({ type: "", message: "" });
    try {
      await resetStudentPassword(selectedStudent._id, resetPasswordVal);
      setActionFeedback({
        type: "success",
        message: "User password has been successfully reset!",
      });
      setResetPasswordVal("");
    } catch (err) {
      setActionFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to reset password.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // Filter students based on active role tab and query terms
  const filteredStudents = students.filter((student) => {
    const roleMatch = (student.role || "student") === activeTab;
    const nameMatch = (student.name + " " + (student.surname || ""))
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    
    // Admins don't have degree/faculty constraints typically
    const facultyMatch = facultyFilter && activeTab === "student"
      ? student.faculty === facultyFilter
      : true;
    const degreeMatch = degreeFilter && activeTab === "student"
      ? student.degree === degreeFilter
      : true;

    return roleMatch && nameMatch && facultyMatch && degreeMatch;
  });

  const uniqueFaculties = [
    ...new Set(students.filter(s => s.role === "student").map((student) => student.faculty)),
  ].filter(Boolean);
  
  const uniqueDegrees = [
    ...new Set(students.filter(s => s.role === "student").map((student) => student.degree)),
  ].filter(Boolean);

  // Pagination Calculations
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const activePage = currentPage > totalPages ? 1 : currentPage;
  const paginatedStudents = filteredStudents.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );

  const cardStyle = {
    backgroundColor: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    borderTop: "4px solid var(--color-primary)",
    boxShadow: "var(--shadow-sm)",
    borderRadius: "var(--radius-lg)",
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <div className="error-message">{error}</div>
      </Container>
    );
  }

  return (
    <div
      className="d-flex flex-column flex-lg-row"
      style={{
        maxWidth: "100vw",
        overflowX: "hidden",
        backgroundColor: "var(--color-bg)"
      }}>
      {/* Sidebar */}
      <Sidebar />

      <Container
        fluid
        className="py-3 px-4"
        style={{ minHeight: "100vh", maxHeight: "100vh", overflowY: "scroll" }}>
        {/* Top Bar */}
        <TopNavBar />

        {/* Greeting Section */}
        <Row className="align-items-center justify-content-between mt-3">
          <Col xs={10} md={6}>
            <div className="d-flex flex-column text-start">
              <h3 className="fw-bold mb-1" style={{ color: "var(--color-primary)" }}>
                {getGreeting()}, {user?.name || "Admin"}
              </h3>
              <p className="mb-0 text-muted" style={{ fontSize: "0.95rem" }}>
                View and manage registered user profiles, passwords, and security clearance.
              </p>
            </div>
          </Col>
          <Col
            xs={2}
            md={6}
            className="d-flex align-items-center justify-content-end mt-3 mt-md-0">
            <div className="ms-2">
              <NotificationIcon />
            </div>
          </Col>
        </Row>

        <hr style={{ color: "var(--color-border)", width: "100%", margin: "20px 0" }} />

        {/* User Base Navigation Tabs */}
        <div className="d-flex border-bottom mb-3 bg-white p-2 rounded shadow-sm gap-2" style={{ backgroundColor: "var(--color-surface)", borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
          <Button
            onClick={() => {
              setActiveTab("student");
              setCurrentPage(1);
              setFacultyFilter("");
              setDegreeFilter("");
            }}
            className="fw-bold px-4 py-2 border-0"
            style={{ 
              borderRadius: "var(--radius-md)", 
              backgroundColor: activeTab === "student" ? "var(--color-primary)" : "rgba(27, 58, 135, 0.05)",
              color: activeTab === "student" ? "#fff" : "var(--color-text-secondary)",
              transition: "all var(--transition)"
            }}
          >
            Student Volunteers ({students.filter(s => (s.role || "student") === "student").length})
          </Button>
          <Button
            onClick={() => {
              setActiveTab("admin");
              setCurrentPage(1);
              setFacultyFilter("");
              setDegreeFilter("");
            }}
            className="fw-bold px-4 py-2 border-0"
            style={{ 
              borderRadius: "var(--radius-md)", 
              backgroundColor: activeTab === "admin" ? "var(--color-primary)" : "rgba(27, 58, 135, 0.05)",
              color: activeTab === "admin" ? "#fff" : "var(--color-text-secondary)",
              transition: "all var(--transition)"
            }}
          >
            System Administrators ({students.filter(s => s.role === "admin").length})
          </Button>
          <Button
            onClick={() => {
              setActiveTab("organization");
              setCurrentPage(1);
              setFacultyFilter("");
              setDegreeFilter("");
            }}
            className="fw-bold px-4 py-2 border-0"
            style={{
              borderRadius: "var(--radius-md)",
              backgroundColor: activeTab === "organization" ? "var(--color-primary)" : "rgba(27, 58, 135, 0.05)",
              color: activeTab === "organization" ? "#fff" : "var(--color-text-secondary)",
            }}
          >
            Organisations ({students.filter(s => s.role === "organization").length})
          </Button>
          {activeTab === "admin" && (
            <Button
              className="fw-bold px-3 py-2 btn-wcco-primary border-0 ms-auto"
              style={{ borderRadius: "var(--radius-md)" }}
              onClick={() => setShowCreateAdminModal(true)}
            >
              + Create Admin
            </Button>
          )}
        </div>

        {/* Listing Container */}
        <Card className="mb-4 shadow-sm border-0" style={cardStyle}>
          <Card.Body className="p-4">
            {/* Filters Row */}
            <Row className="mb-4 g-3 align-items-center">
              <Col md={activeTab === "student" ? 4 : 12}>
                <Form.Control
                  type="text"
                  placeholder={activeTab === "student" ? "Search by student name..." : "Search by admin name..."}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to page 1 on search
                  }}
                  style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}
                />
              </Col>
              
              {activeTab === "student" && (
                <>
                  <Col md={4}>
                    <Form.Select
                      value={facultyFilter}
                      onChange={(e) => {
                        setFacultyFilter(e.target.value);
                        setCurrentPage(1); // Reset to page 1 on filter
                      }}
                      style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}>
                      <option value="">All Faculties</option>
                      {uniqueFaculties.map((faculty) => (
                        <option key={faculty} value={faculty}>
                          {faculty}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={4}>
                    <Form.Select
                      value={degreeFilter}
                      onChange={(e) => {
                        setDegreeFilter(e.target.value);
                        setCurrentPage(1); // Reset to page 1 on filter
                      }}
                      style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}>
                      <option value="">All Degrees</option>
                      {uniqueDegrees.map((degree) => (
                        <option key={degree} value={degree}>
                          {degree}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </>
              )}
            </Row>

            {filteredStudents.length > 0 ? (
              <div className="table-responsive">
                <Table hover align="middle" className="mb-0">
                  <thead style={{ backgroundColor: "rgba(27, 58, 135, 0.05)", borderBottom: "2px solid var(--color-border)" }}>
                    <tr>
                      <th className="text-start py-3" style={{ borderTopLeftRadius: "var(--radius-md)", color: "var(--color-primary)", fontWeight: "700" }}>Name</th>
                      <th className="text-start py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Surname</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>{activeTab === "student" ? "Student Number" : "System ID"}</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "700" }}>Email</th>
                      <th className="text-end py-3" style={{ borderTopRightRadius: "var(--radius-md)", paddingRight: "20px", color: "var(--color-primary)", fontWeight: "700" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedStudents.map((student) => (
                      <tr 
                        key={student._id}
                        onClick={() => handleViewStudent(student)}
                        style={{ cursor: "pointer", transition: "background-color var(--transition)" }}
                        className="align-middle"
                      >
                        <td className="text-start fw-bold py-3" style={{ color: "var(--color-text)" }}>
                          {student.organizationName || student.name}
                        </td>
                        <td className="text-start py-3 fw-semibold" style={{ color: "var(--color-text-secondary)" }}>
                          {student.surname}
                        </td>
                        <td className="py-3 fw-bold" style={{ color: "var(--color-primary)" }}>
                          {student.studentNumber || student._id.toString().substring(0, 8).toUpperCase()}
                        </td>
                        <td className="py-3 text-muted" style={{ color: "var(--color-text-muted)" }}>
                          {student.email}
                        </td>
                        <td className="text-end py-3" style={{ paddingRight: "20px" }} onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="light"
                            size="sm"
                            className="fw-semibold px-3 py-1.5"
                            style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", color: "var(--color-primary)", backgroundColor: "var(--color-surface)" }}
                            onClick={() => handleViewStudent(student)}>
                            <FiEye className="me-1" /> View Profile
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="d-flex justify-content-between align-items-center mt-4 pt-3 border-top">
                    <div className="text-muted small">
                      Showing <strong>{(activePage - 1) * itemsPerPage + 1}</strong> to{" "}
                      <strong>{Math.min(activePage * itemsPerPage, filteredStudents.length)}</strong> of{" "}
                      <strong>{filteredStudents.length}</strong> accounts
                    </div>
                    <div className="d-flex gap-1">
                      <Button
                        variant="light"
                        size="sm"
                        disabled={activePage === 1}
                        onClick={() => setCurrentPage(activePage - 1)}
                        style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                      >
                        Previous
                      </Button>
                      {[...Array(totalPages)].map((_, idx) => {
                        const pageNum = idx + 1;
                        const isActive = pageNum === activePage;
                        return (
                          <Button
                            key={pageNum}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            style={{
                              backgroundColor: isActive ? "var(--color-primary)" : "var(--color-surface)",
                              color: isActive ? "#fff" : "var(--color-text-secondary)",
                              border: `1px solid ${isActive ? "var(--color-primary)" : "var(--color-border)"}`,
                              borderRadius: "var(--radius-sm)",
                              fontWeight: "600",
                              minWidth: "32px"
                            }}
                          >
                            {pageNum}
                          </Button>
                        );
                      })}
                      <Button
                        variant="light"
                        size="sm"
                        disabled={activePage === totalPages}
                        onClick={() => setCurrentPage(activePage + 1)}
                        style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted mb-0">No registered accounts found in this category.</p>
              </div>
            )}

            {/* View Profile Modal */}
            {selectedStudent && (
              <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }} className="pb-3">
                  <Modal.Title className="fw-bold" style={{ color: "var(--color-primary)" }}>
                    Account Profile: {selectedStudent.name} {selectedStudent.surname}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3 modal-body-scroll">
                  <Card className="border-0 shadow-none" style={{ backgroundColor: "var(--color-bg)", borderRadius: "var(--radius-lg)" }}>
                    <Card.Body className="p-4">
                      <h5 className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>Personal & Role Info</h5>
                      <Table hover size="sm" className="mb-4">
                        <tbody>
                          <tr>
                            <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Security Privilege</th>
                            <td className="py-2">
                              <span className={`px-2 py-1 fw-bold ${selectedStudent.role === "admin" ? "badge-red" : "badge-primary"}`} style={{ fontSize: "0.75rem", borderRadius: "12px" }}>
                                {selectedStudent.role?.toUpperCase() || "STUDENT"}
                              </span>
                            </td>
                          </tr>
                          {selectedStudent.role === "student" && (
                            <tr>
                              <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Student Number</th>
                              <td className="py-2 fw-bold" style={{ color: "var(--color-primary)" }}>{selectedStudent.studentNumber}</td>
                            </tr>
                          )}
                          <tr>
                            <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Email Address</th>
                            <td className="py-2" style={{ color: "var(--color-text)" }}>{selectedStudent.email}</td>
                          </tr>
                          <tr>
                            <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Contact Number</th>
                            <td className="py-2" style={{ color: "var(--color-text)" }}>{selectedStudent.contactNumber || "N/A"}</td>
                          </tr>
                          {selectedStudent.role === "student" && (
                            <>
                              <tr>
                                <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Degree Program</th>
                                <td className="py-2 fw-semibold" style={{ color: "var(--color-text-secondary)" }}>{selectedStudent.degree}</td>
                              </tr>
                              <tr>
                                <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Faculty</th>
                                <td className="py-2" style={{ color: "var(--color-text)" }}>{selectedStudent.faculty}</td>
                              </tr>
                              <tr>
                                <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Volunteering Interests</th>
                                <td className="py-2" style={{ color: "var(--color-text-secondary)" }}>
                                  {selectedStudent.volunteeringInterests?.join(", ") || "None specified"}
                                </td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </Table>

                      {/* Display Active Participations only for Students */}
                      {selectedStudent.role === "student" && (
                        <>
                          <h5 className="fw-bold mb-3 mt-4" style={{ color: "var(--color-primary)" }}>Active Participations</h5>
                          {selectedStudent.participations && selectedStudent.participations.length > 0 ? (
                            selectedStudent.participations.map((participation) => (
                              <Card key={participation._id} className="mb-3 shadow-none" style={{ borderRadius: "var(--radius-md)", border: "1px solid var(--color-border)", backgroundColor: "var(--color-surface)" }}>
                                <Card.Body className="p-3">
                                  <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "1.1rem", color: "var(--color-text)" }}>
                                    {participation.project?.name || "Volunteer Program"}
                                  </h6>
                                  <div className="d-flex justify-content-between text-muted small mb-2">
                                    <span>Role: <strong>{participation.role}</strong></span>
                                    <span>Joined: <strong>{new Date(participation.dateJoined).toLocaleDateString()}</strong></span>
                                  </div>
                                  <div className="d-flex gap-2 flex-wrap">
                                    <span className="badge-teal px-2 py-1" style={{ fontSize: "0.75rem", borderRadius: "12px" }}>Approved: {participation.approvedHours} hrs</span>
                                    <span className="badge-gold px-2 py-1" style={{ fontSize: "0.75rem", borderRadius: "12px" }}>Pending: {participation.pendingHours} hrs</span>
                                    <span className="badge-primary px-2 py-1" style={{ fontSize: "0.75rem", borderRadius: "12px" }}>Total: {participation.totalHours} hrs</span>
                                  </div>
                                </Card.Body>
                              </Card>
                            ))
                          ) : (
                            <p className="text-muted small">No active program participations registered.</p>
                          )}
                        </>
                      )}

                      {/* Direct Consultation Panel */}
                      <div className="d-flex justify-content-between align-items-center p-3 mb-4 rounded border bg-white" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                        <div className="text-start">
                          <h6 className="fw-bold text-dark mb-1">Direct Consultation</h6>
                          <p className="text-muted small mb-0">Start a private consultation message thread with this student.</p>
                        </div>
                        <Button 
                          className="fw-bold px-3 py-2 border-0 btn-wcco-teal"
                          onClick={() => {
                            setShowModal(false);
                            navigate(`/messages?userId=${selectedStudent._id}`);
                          }}
                        >
                          Send Message
                        </Button>
                      </div>

                      {/* Administrative Security Actions Section */}
                      <hr className="my-4" style={{ color: "var(--color-border)" }} />
                      <h5 className="fw-bold mb-3 text-danger">Administrative System Security</h5>
                      
                      {actionFeedback.message && (
                        <Alert variant={actionFeedback.type} className="py-2.5 px-3 mb-3 small text-start">
                          {actionFeedback.message}
                        </Alert>
                      )}

                      <div className="d-flex flex-column flex-md-row gap-3 align-items-md-stretch">
                        {/* Privilege Promotion */}
                        <div className="flex-grow-1 p-3 border rounded text-start bg-white d-flex flex-column justify-content-between" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                          <div>
                            <h6 className="fw-bold text-dark small mb-1">User Privilege Access</h6>
                            <p className="text-muted small mb-3">
                              Toggle administrative security credentials.
                            </p>
                          </div>
                          <div>
                            <Button
                              size="sm"
                              disabled={actionLoading || selectedStudent._id === user?._id}
                              onClick={handleRoleToggle}
                              className="fw-bold w-100 py-2 border-0"
                              style={{ 
                                backgroundColor: selectedStudent.role === "admin" ? "var(--color-gold)" : "var(--color-primary)",
                                color: "#fff",
                                borderRadius: "var(--radius-sm)"
                              }}
                            >
                              {selectedStudent.role === "admin" ? "Demote to Student" : "Promote to Admin"}
                            </Button>
                            {selectedStudent._id === user?._id && (
                              <span className="text-danger mt-2 d-block text-center" style={{ fontSize: "0.75rem", fontWeight: "600" }}>
                                Self-demotion is locked.
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Reset User Password */}
                        <div className="flex-grow-1 p-3 border rounded text-start bg-white d-flex flex-column justify-content-between" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                          <div>
                            <h6 className="fw-bold text-dark small mb-1">Administrative Force-Reset Password</h6>
                            <p className="text-muted small mb-3">
                              Update user's password directly. Use at least 8 characters with uppercase,
                              lowercase, a number, and a special character.
                            </p>
                          </div>
                          <Form onSubmit={handleAdminResetPassword} className="d-flex gap-2">
                            <Form.Control
                              type="password"
                              placeholder="Type new password..."
                              size="sm"
                              value={resetPasswordVal}
                              onChange={(e) => setResetPasswordVal(e.target.value)}
                              minLength={8}
                              required
                              style={{ borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)" }}
                            />
                            <Button 
                              type="submit" 
                              size="sm" 
                              disabled={actionLoading}
                              className="fw-bold px-3 shrink-0 border-0"
                              style={{ backgroundColor: "var(--color-red)", color: "#fff", borderRadius: "var(--radius-sm)" }}
                            >
                              Reset
                            </Button>
                          </Form>
                        </div>

                        {/* Delete User Account */}
                        <div className="flex-grow-1 p-3 border rounded text-start bg-white d-flex flex-column justify-content-between" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                          <div>
                            <h6 className="fw-bold text-dark small mb-1">Delete User Account</h6>
                            <p className="text-muted small mb-3">
                              Permanently remove this account and associated hours/logs.
                            </p>
                          </div>
                          <div>
                            <Button
                              size="sm"
                              disabled={actionLoading || selectedStudent._id === user?._id}
                              onClick={() => setShowDeleteConfirm(true)}
                              className="fw-bold w-100 py-2 border-0"
                              style={{ 
                                backgroundColor: "var(--color-red)",
                                color: "#fff",
                                borderRadius: "var(--radius-sm)"
                              }}
                            >
                              Delete Account
                            </Button>
                            {selectedStudent._id === user?._id && (
                              <span className="text-danger mt-2 d-block text-center" style={{ fontSize: "0.75rem", fontWeight: "600" }}>
                                Self-deletion is locked.
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Modal.Body>
                <Modal.Footer className="border-0">
                  <Button variant="secondary" className="px-4" style={{ borderRadius: "var(--radius-sm)" }} onClick={handleCloseModal}>
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>
            )}

            {/* Create Admin Modal */}
            <Modal show={showCreateAdminModal} onHide={() => { if (!createAdminLoading) setShowCreateAdminModal(false); }} centered>
              <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }} className="pb-3">
                <Modal.Title className="fw-bold" style={{ color: "var(--color-primary)" }}>
                  Create Admin Account
                </Modal.Title>
              </Modal.Header>
              <Form onSubmit={handleCreateAdminSubmit}>
                <Modal.Body className="pt-3">
                  {createAdminFeedback.message && (
                    <Alert variant={createAdminFeedback.type} className="py-2.5 px-3 mb-3 small text-start">
                      {createAdminFeedback.message}
                    </Alert>
                  )}
                  <Row className="g-3 text-start">
                    <Col xs={12} sm={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={newAdminData.name}
                          onChange={(e) => setNewAdminData({ ...newAdminData, name: e.target.value })}
                          required
                          placeholder="Enter first name"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Surname</Form.Label>
                        <Form.Control
                          type="text"
                          name="surname"
                          value={newAdminData.surname}
                          onChange={(e) => setNewAdminData({ ...newAdminData, surname: e.target.value })}
                          required
                          placeholder="Enter surname"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Staff Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={newAdminData.email}
                          onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                          required
                          placeholder="name.surname@wits.ac.za"
                          pattern="^[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+@wits\.ac\.za$"
                          title="Staff email must be in the format name.surname@wits.ac.za"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: "0.78rem" }}>
                          Must be a valid Wits staff email domain.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={newAdminData.password}
                          onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                          required
                          minLength={6}
                          placeholder="Enter account password (min 6 chars)"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                  <Button
                    variant="secondary"
                    className="px-4"
                    disabled={createAdminLoading}
                    style={{ borderRadius: "var(--radius-sm)" }}
                    onClick={() => setShowCreateAdminModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createAdminLoading}
                    className="px-4 border-0 btn-wcco-primary"
                    style={{ borderRadius: "var(--radius-sm)" }}
                  >
                    {createAdminLoading ? "Creating..." : "Create Account"}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>

            {/* Delete User Confirmation Modal */}
            {selectedStudent && (
              <Modal show={showDeleteConfirm} onHide={() => { if (!actionLoading) setShowDeleteConfirm(false); }} centered size="md">
                <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }} className="pb-3">
                  <Modal.Title className="fw-bold text-danger">
                    Confirm Account Deletion
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                  <p className="text-start mb-3">
                    Are you sure you want to permanently delete the account for <strong>{selectedStudent.name} {selectedStudent.surname}</strong> ({selectedStudent.email})?
                  </p>
                  <p className="text-start text-danger mb-4 small fw-semibold">
                    WARNING: This action is irreversible. All logged volunteering hours, history, and records associated with this account will be permanently erased.
                  </p>
                  <Form onSubmit={(e) => { e.preventDefault(); if (deleteConfirmInput === "DELETE") handleDeleteUserSubmit(); }}>
                    <Form.Group className="mb-3 text-start">
                      <Form.Label style={{ fontSize: "0.85rem", fontWeight: 600 }}>Type <strong>DELETE</strong> to confirm:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Type DELETE..."
                        value={deleteConfirmInput}
                        onChange={(e) => setDeleteConfirmInput(e.target.value)}
                        required
                        style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="secondary"
                        disabled={actionLoading}
                        style={{ borderRadius: "var(--radius-sm)" }}
                        onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmInput(""); }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={actionLoading || deleteConfirmInput !== "DELETE"}
                        style={{ backgroundColor: "var(--color-red)", color: "#fff", borderRadius: "var(--radius-sm)", border: "none" }}
                      >
                        {actionLoading ? "Deleting..." : "Permanently Delete"}
                      </Button>
                    </div>
                  </Form>
                </Modal.Body>
              </Modal>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default AdminStudentsView;
